import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Search } from 'lucide-react';
import * as LucideIcons from 'lucide-react';

// List of commonly used icons
const ICON_LIST = [
  'Briefcase', 'Clipboard', 'CheckCircle', 'Circle', 'Target', 'Flag',
  'Star', 'Heart', 'TrendingUp', 'Zap', 'Gift', 'Award',
  'Bell', 'Calendar', 'Clock', 'Timer', 'AlarmClock', 'Watch',
  'File', 'FileText', 'Folder', 'Archive', 'Package', 'Inbox',
  'Users', 'User', 'UserPlus', 'UserCheck', 'UserCircle', 'Shield',
  'Settings', 'Tool', 'Wrench', 'Hammer', 'Cog', 'Sliders',
  'Map', 'MapPin', 'Navigation', 'Compass', 'Globe', 'Home',
  'Building', 'Store', 'Warehouse', 'Factory', 'Truck', 'Car',
  'Plane', 'Ship', 'Train', 'Bike', 'Bus', 'Rocket',
  'Box', 'Package2', 'Container', 'Boxes', 'Archive', 'Database',
  'Server', 'HardDrive', 'Cpu', 'Monitor', 'Smartphone', 'Tablet',
  'Code', 'Terminal', 'GitBranch', 'GitCommit', 'GitPullRequest', 'Github',
  'Mail', 'MessageSquare', 'MessageCircle', 'Send', 'AtSign', 'Phone',
  'Mic', 'Video', 'Camera', 'Image', 'Film', 'Music',
  'DollarSign', 'CreditCard', 'Wallet', 'PiggyBank', 'TrendingDown', 'BarChart',
  'PieChart', 'Activity', 'Layers', 'Layout', 'Grid', 'List',
  'Book', 'BookOpen', 'Bookmark', 'GraduationCap', 'School', 'Library',
  'Coffee', 'Pizza', 'Utensils', 'ShoppingCart', 'ShoppingBag', 'Tag',
  'Sun', 'Moon', 'Cloud', 'CloudRain', 'CloudSnow', 'Zap',
  'Flame', 'Droplet', 'Wind', 'ThermometerSun', 'Snowflake', 'Umbrella',
  'Lock', 'Unlock', 'Key', 'Eye', 'EyeOff', 'AlertCircle',
  'Info', 'HelpCircle', 'AlertTriangle', 'XCircle', 'Check', 'X',
  'Plus', 'Minus', 'Edit', 'Trash2', 'Copy', 'Scissors',
  'Link', 'Paperclip', 'Download', 'Upload', 'Share2', 'ExternalLink',
];

export function IconPicker({ open, onOpenChange, onSelectIcon, currentIcon }) {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredIcons = ICON_LIST.filter(iconName =>
    iconName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleIconClick = (iconName) => {
    onSelectIcon(iconName);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Choose an icon</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search icons..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Icon Grid */}
          <ScrollArea className="h-[400px] border rounded-md p-4">
            <div className="grid grid-cols-8 gap-2">
              {filteredIcons.map((iconName) => {
                const IconComponent = LucideIcons[iconName];
                if (!IconComponent) return null;

                return (
                  <button
                    key={iconName}
                    onClick={() => handleIconClick(iconName)}
                    className={`p-3 rounded-md hover:bg-muted transition-colors ${
                      currentIcon === iconName ? 'bg-primary text-primary-foreground' : ''
                    }`}
                    title={iconName}
                  >
                    <IconComponent className="h-5 w-5 mx-auto" />
                  </button>
                );
              })}
            </div>
          </ScrollArea>

          {/* Footer */}
          <div className="flex justify-between items-center text-sm text-muted-foreground">
            <span>{filteredIcons.length} icons</span>
            {currentIcon && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  onSelectIcon(null);
                  onOpenChange(false);
                }}
              >
                Remove icon
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
